// Parsing and matching for bulk device selection by serial number (pasted
// text, the device list exported from MagicInfo, or a CSV file). Matching is
// case-insensitive and ignores surrounding spaces and quotes.

import { readXlsRows } from './xls-reader';

export interface ISerialDevice {
  serial?: string | null;
}

export interface ISerialMatchResult<T> {
  /** Devices found, in the order their serials were given. */
  matched: T[];
  /** Serials not found among the available or already assigned devices. */
  notFound: string[];
  /** Serials of devices that are already assigned (nothing to add). */
  alreadyAssigned: string[];
  /** Serials given more than once (repeats are ignored). */
  duplicates: number;
}

export interface ISerialCsvResult {
  serials: string[];
  error?: string;
}

export const SERIAL_CSV_HEADER = 'serial';

// "Device Serial" is the column of the MagicInfo device list export.
const SERIAL_HEADERS = ['serial', 'serials', 'serial number', 'serial_number', 'serialnumber', 'serial no', 'sn', 'device serial'];
const NO_SERIAL_COLUMN = 'No serial column found. Upload the device list exported from MagicInfo, or a CSV with a "serial" column.';
const UNREADABLE_XLS = 'The file could not be read. Export the device list from MagicInfo again, or upload a CSV.';

/** Splits pasted text on commas, semicolons, tabs, spaces and new lines. */
export function parseSerials(text: string): string[] {
  return (text || '')
    .split(/[\s,;]+/)
    .map(cleanCell)
    .filter((serial) => !!serial);
}

/**
 * Reads serials from a CSV file (comma or semicolon separated): the serial
 * column when there is a header, or every value of a one-column file.
 */
export function parseSerialsCsv(content: string): ISerialCsvResult {
  const lines = (content || '')
    .replace(/^﻿/, '')
    .split(/\r?\n/)
    .filter((line) => line.trim().length > 0);
  const delimiter = lines.length && lines[0].includes(';') && !lines[0].includes(',') ? ';' : ',';
  return serialsFromRows(lines.map((line) => line.split(delimiter)));
}

/** Reads serials from an Excel 97-2003 (.xls) file, such as the device list exported from MagicInfo. */
export function parseSerialsXls(data: ArrayBuffer | Uint8Array): ISerialCsvResult {
  let rows: string[][];
  try {
    rows = readXlsRows(data);
  } catch {
    return { serials: [], error: UNREADABLE_XLS };
  }
  return serialsFromRows(rows);
}

/**
 * The values below the first row that has a serial header, or every value of
 * a one-column table without a header.
 */
function serialsFromRows(table: string[][]): ISerialCsvResult {
  const rows = table.map((row) => row.map((cell) => cleanCell(cell || ''))).filter((row) => row.some((cell) => !!cell));
  if (!rows.length) {
    return { serials: [], error: 'The file is empty.' };
  }

  for (let i = 0; i < rows.length; i++) {
    const serialColumn = rows[i].findIndex((cell) => SERIAL_HEADERS.includes(cell.toLowerCase()));
    if (serialColumn >= 0) {
      return { serials: rows.slice(i + 1).map((row) => row[serialColumn] || '').filter((serial) => !!serial) };
    }
  }
  if (rows.every((row) => row.length === 1)) {
    return { serials: rows.map((row) => row[0]) };
  }
  return { serials: [], error: NO_SERIAL_COLUMN };
}

export function matchSerials<T extends ISerialDevice>(
  serials: string[],
  available: T[],
  assigned: ISerialDevice[] = []
): ISerialMatchResult<T> {
  const availableBySerial = new Map<string, T>();
  for (const device of available) {
    const key = serialKey(device.serial);
    if (key && !availableBySerial.has(key)) availableBySerial.set(key, device);
  }
  const assignedSerials = new Set(assigned.map((device) => serialKey(device.serial)).filter((key) => !!key));

  const result: ISerialMatchResult<T> = { matched: [], notFound: [], alreadyAssigned: [], duplicates: 0 };
  const seen = new Set<string>();
  for (const serial of serials) {
    const key = serialKey(serial);
    if (!key) continue;
    if (seen.has(key)) {
      result.duplicates++;
      continue;
    }
    seen.add(key);

    const device = availableBySerial.get(key);
    if (device) result.matched.push(device);
    else if (assignedSerials.has(key)) result.alreadyAssigned.push(serial);
    else result.notFound.push(serial);
  }
  return result;
}

/** One-column CSV with a header row; values with commas or quotes are quoted. */
export function toCsv(header: string, values: string[]): string {
  const escape = (value: string) => /[",;\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
  return [header, ...values].map(escape).join('\n') + '\n';
}

function serialKey(serial: string | null | undefined): string {
  return cleanCell(serial || '').toUpperCase();
}

function cleanCell(value: string): string {
  return value.trim().replace(/^"(.*)"$/, '$1').trim();
}
